import { useEffect, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Link2,
  Link2Off,
  Image as ImageIcon,
  Undo,
  Redo,
  RemoveFormatting,
} from "lucide-react";

/**
 * TiptapEditor — headless rich text editor styled with Tailwind to match the admin dark
 * theme. Round-trips HTML through `editor.getHTML()` so it stays compatible with the
 * existing `body_html` storage on the backend (no schema migration needed).
 *
 * Props:
 *   value    — controlled HTML string
 *   onChange — receives updated HTML on every edit
 *   testId   — root data-testid (buttons get suffixed testids for reliable QA)
 */
export function TiptapEditor({ value = "", onChange, testId = "tiptap-editor" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // Keep paragraph/lists/blockquote/hr/code from StarterKit; disable link so we can
        // configure it explicitly below with `openOnClick: false`.
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({
        HTMLAttributes: { class: "rounded-md max-w-full h-auto my-2" },
      }),
      Placeholder.configure({
        placeholder: "Start typing… Use the toolbar above to format text.",
      }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      // Tiptap treats an empty doc as `<p></p>`. Emit an empty string instead so save
      // validation (min_length=1) treats that as "no content" rather than falsely dirty.
      const html = ed.isEmpty ? "" : ed.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-invert max-w-none min-h-[24rem] rounded-md border border-border " +
          "bg-elevated px-4 py-3 text-sm text-text focus:border-gold focus:outline-none " +
          "focus:ring-1 focus:ring-gold [&_a]:text-gold [&_h2]:text-text [&_h3]:text-text " +
          "[&_.ProseMirror]:min-h-[20rem] [&_.ProseMirror:focus]:outline-none",
      },
    },
  });

  // Keep the editor content in sync when the parent swaps `value` (e.g., switching CMS tabs).
  // We only push external changes when they meaningfully differ to avoid selection resets on
  // every keystroke.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && !(editor.isEmpty && !value)) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;                       // user cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Image URL");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <Toolbar editor={editor} onLink={setLink} onImage={addImage} testId={testId} />
      <EditorContent editor={editor} data-testid={`${testId}-content`} />
    </div>
  );
}

function Toolbar({ editor, onLink, onImage, testId }) {
  return (
    <div
      className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-card/60 p-1.5"
      data-testid={`${testId}-toolbar`}
    >
      <ToolBtn
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
        title="Bold (⌘B)"
        testId={`${testId}-bold`}
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
        title="Italic (⌘I)"
        testId={`${testId}-italic`}
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolBtn>
      <Divider />
      <ToolBtn
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive("heading", { level: 2 })}
        title="Heading 2"
        testId={`${testId}-h2`}
      >
        <Heading2 className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive("heading", { level: 3 })}
        title="Heading 3"
        testId={`${testId}-h3`}
      >
        <Heading3 className="h-3.5 w-3.5" />
      </ToolBtn>
      <Divider />
      <ToolBtn
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
        title="Bulleted list"
        testId={`${testId}-ul`}
      >
        <List className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
        title="Numbered list"
        testId={`${testId}-ol`}
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive("blockquote")}
        title="Quote"
        testId={`${testId}-quote`}
      >
        <Quote className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        active={false}
        title="Horizontal rule"
        testId={`${testId}-hr`}
      >
        <Minus className="h-3.5 w-3.5" />
      </ToolBtn>
      <Divider />
      <ToolBtn
        onClick={onLink}
        active={editor.isActive("link")}
        title="Add/edit link"
        testId={`${testId}-link`}
      >
        <Link2 className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        onClick={() => editor.chain().focus().unsetLink().run()}
        active={false}
        disabled={!editor.isActive("link")}
        title="Remove link"
        testId={`${testId}-unlink`}
      >
        <Link2Off className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn onClick={onImage} active={false} title="Insert image (URL)" testId={`${testId}-image`}>
        <ImageIcon className="h-3.5 w-3.5" />
      </ToolBtn>
      <Divider />
      <ToolBtn
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        active={false}
        title="Clear formatting"
        testId={`${testId}-clear`}
      >
        <RemoveFormatting className="h-3.5 w-3.5" />
      </ToolBtn>
      <Divider />
      <ToolBtn
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        active={false}
        title="Undo (⌘Z)"
        testId={`${testId}-undo`}
      >
        <Undo className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        active={false}
        title="Redo (⌘⇧Z)"
        testId={`${testId}-redo`}
      >
        <Redo className="h-3.5 w-3.5" />
      </ToolBtn>
    </div>
  );
}

function ToolBtn({ onClick, active, disabled, title, children, testId }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}      /* keep editor focus */
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-testid={testId}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-sm border text-xs transition-colors ${
        active
          ? "border-gold/60 bg-gold/15 text-gold"
          : "border-transparent text-muted hover:border-border hover:bg-elevated hover:text-text"
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span aria-hidden className="mx-0.5 h-5 w-px bg-border" />;
}

export default TiptapEditor;
